/// <reference lib="dom" />
/**
 * storageBackend.ts
 * Single point of access to the browser `Storage` backend for the whole
 * codebase.
 *
 * AGENTS.md requires localStorage access to stay centralised rather than
 * scattered through game code. `saveManager.ts` (game progress) and
 * `userSettings.ts` (UI preferences) are the only two modules that persist
 * anything, and they each owned a byte-identical copy of this accessor —
 * including the same subtle SecurityError guard. A fix applied to one copy
 * silently missed the other, so the guard is defined once here instead.
 *
 * This module deliberately does NOT wrap read/write. The two services store
 * different payloads under different keys with different validation and
 * different failure semantics; merging them would conflate progress with
 * preferences (clearing a save would wipe your settings). Only the backend
 * lookup is shared.
 *
 * @module storageBackend
 */

/**
 * Resolve the `Storage` backend.
 *
 * DECISION: Wrap the property access in try/catch — reading
 * `globalThis.localStorage` throws `SecurityError` in browsers where storage
 * is blocked (file://, sandboxed iframes, third-party-cookie blocking). The
 * throw must not propagate above this helper: `loadUserSettings()` runs from
 * `bootApp()` before any try block, and `SaveManager.load()` would crash
 * before reaching its own guard.
 *
 * @returns The `localStorage` object, or `null` when storage is unavailable
 *          (non-browser test environments, or access blocked by the browser).
 *          Callers must treat `null` as "persistence disabled" and no-op.
 */
export function getStorage(): Storage | null {
  try {
    const g = globalThis as { localStorage?: Storage };
    return g.localStorage ?? null;
  } catch {
    return null;
  }
}
